import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, withRouter } from "react-router-dom/cjs/react-router-dom";
import TranslationService from "../../Services/translationService";
import ArrowBackSvg from "../../Components/Svg/arrowBackSvg";
import ApiService from "../../Services/apiService";
import AlertService from "../../Services/alertService";
import {
	ERROR_KEY,
	NUMBER_KEY,
	VALID_IMAGE_TYPES_KEY,
	WARNING_KEY,
} from "../../Constants/mainKeys";
import uuid from "react-uuid";
import {
	addPartialViewSpinner,
	removePartialViewSpinner,
} from "../../Store/Actions/spinner";
import ReactSelectOption from "../../Components/SelectOption/reactSelectOption";
import Input from "../../Components/Inputs/input";
import Auxiliary from "../../hoc/auxiliary/auxiliary";
import InputCheckBox from "../../Components/Inputs/inputCheckBox";
import ActionButton from "../../Components/ActionButton/actionButton";
import UploadButton from "../../Components/UploadButton/uploadButton";
import CloseSvg from "../../Components/Svg/closeSvg";
import MainService from "../../Services/mainService";

const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value);
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};

const buttonSpinnerId = uuid();

function UserForm(props) {
	const mainService = new MainService();
	const translations = useSelector(state => state.translation.translations);
	const [translationService, setTranslationService] = useState(null);
	const { language, languages } = useSelector(state => state.language);
	const { userId } = props.match.params;
	const dispatch = useDispatch();
	const [values, setValues] = useState({
		firstname: "",
		lastname: "",
		middlename: "",
		email: "",
		phone: "",
		dob: "",
		image: "",
		userTypeId: null,
		organizationId: "",
		isUserId: "",
		tziidDisplayname: "",
		sendEmail: true,
		sendNotification: true,
		username: "",
		mobilePhone: "",
		addressResidence: "",
		addressRegistration: "",
		defaultLanguageId: null,
		password: "",
	});
	const [isInvalidSubmit, setIsInvalidSubmit] = useState(false);
	const [users, setUsers] = useState([]);
	const [timeZones, setTimeZones] = useState([]);
	const [userTypes, setUserTypes] = useState([]);
	const usernameDebounce = useDebounce(values.username, 1000);
	const phoneDebounce = useDebounce(values.phone, 1000);
	const emailDebounce = useDebounce(values.email, 1000);
	const mobilePhoneDebounce = useDebounce(values.username, 1000);
	const [uploadedImage, setUploadedImage] = useState(null);

	useEffect(() => {
		setTranslationService(new TranslationService(translations));
	}, [translations]);

	useEffect(() => {
		getUsertypes();
		getTimeZone();
	}, []);

	useEffect(() => {
		if (
			!userId &&
			(usernameDebounce ||
				phoneDebounce ||
				emailDebounce ||
				mobilePhoneDebounce)
		) {
			getUsers();
		}
	}, [
		userId,
		usernameDebounce,
		phoneDebounce,
		emailDebounce,
		mobilePhoneDebounce,
	]);

	const getUsers = () => {
		ApiService.filterUsers({
			email: values.email,
			mobilePhone: values.mobilePhone,
			phone: values.phone,
			username: values.username,
		})
			.then(response => {
				if (response && response.data && response.data.length) {
					setUsers(response.data);
				}
			})
			.catch();
	};

	const onChange = (event, fieldname, maxLength = Infinity) => {
		if (maxLength && maxLength < event.target.value.length) {
			return false;
		}
		setValues(values => ({
			...values,
			[fieldname]: event.target.value,
		}));
		setIsInvalidSubmit(false);
	};

	const uploadImage = event => {
		const file = event.target.files[0];
		mainService
			.readFile(file, VALID_IMAGE_TYPES_KEY)
			.then(image => {
				if (!image) {
					return;
				}
				setUploadedImage(image);
				setValues(values => ({ ...values, image: file }));
			})
			.catch(error => {
				AlertService.alert(
					WARNING_KEY,
					translationService.translate("TR_VALID_IMAGE_ERROR_MSG"),
				);
			});
	};

	const removeImg = () => {
		setUploadedImage(null);
		setValues(values => ({ ...values, image: null }));
	};

	const onNumberChange = (event, fieldname, maxValue = null) => {
		if (event.target.value.includes("e") || event.target.value.includes(".")) {
			return false;
		}
		if (event.target.value === "" || Number.isInteger(+event.target.value)) {
			setValues(values => ({ ...values, [fieldname]: event.target.value }));
		}
	};

	const onSelectOptionChange = (item, key) => {
		if (!item) return null;
		setIsInvalidSubmit(false);
		setValues(values => ({ ...values, [key]: item.value }));
	};

	const getTimeZone = () => {
		const spinnerId = uuid();
		dispatch(addPartialViewSpinner(spinnerId));
		ApiService.getTimeZone()
			.then(response => {
				if (
					response &&
					response.data &&
					response.data.timezones &&
					response.data.timezones.length
				) {
					setTimeZones(response.data.timezones);
				}
			})
			.catch(error =>
				AlertService.alert(
					AlertService.checkMessageType(error.respcode) || ERROR_KEY,
					error,
				),
			)
			.finally(() => {
				dispatch(removePartialViewSpinner(spinnerId));
			});
	};

	const getUsertypes = () => {
		const spinnerId = uuid();
		dispatch(addPartialViewSpinner(spinnerId));
		ApiService.getUsertypes(null)
			.then(response => {
				if (response && response.data && response.data.length) {
					const userTyps = [...response.data];
					setUserTypes(userTyps);
				}
			})
			.catch(error =>
				AlertService.alert(
					AlertService.checkMessageType(error.respcode) || ERROR_KEY,
					error,
				),
			)
			.finally(() => {
				dispatch(removePartialViewSpinner(spinnerId));
			});
	};

	const generateRandomPassword = length => {
		const charset =
			"abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
		let password = "";
		for (let i = 0; i < length; i++) {
			const randomIndex = Math.floor(Math.random() * charset.length);
			password += charset[randomIndex];
		}
		return password;
	};
	return (
		translationService && (
			<div className="container">
				<div className="row">
					<div className="col-12 content-title">
						<div className="d-flex align-items-center">
							<Link
								to={`/${language}/users`}
								title={translationService.translate("TR_BACK")}>
								<ArrowBackSvg />
							</Link>
							<h2 className="title">
								{!userId ? "Create User" : "Update User"}
							</h2>
						</div>
					</div>
				</div>
				<hr />
				{users && !!users.length && (
					<Auxiliary>
						<div className="row">
							<div className="col-12">
								<div className="border p-3 rounded">
									<label>
										{translationService.translate("TR_SELECTEXISTINGUSER")}
									</label>
									<div className="d-flex flex-wrap">
										{users.map((item, index) => (
											<button
												key={index}
												className="btn mindalay--btn-default w-100 mt-2 ml-2"
												onClick={() => {}}>
												{item.fullName}
											</button>
										))}
									</div>
								</div>
							</div>
						</div>
						<hr className="mx-2" />
					</Auxiliary>
				)}

				<form onSubmit={() => {}}>
					<div className="row">
						<div className="col-12">
							<div className="row">
								<div className="col-12 col-md-4 mt-2">
									<div className="form-group">
										<label>User types*</label>
										<ReactSelectOption
											value={values.usertypeId}
											selectedValue={(() => {
												const selectedItem = {
													...userTypes.find(
														data => data.id === values.usertypeId,
													),
												};
												if (Object.keys(selectedItem).length) {
													selectedItem.label = selectedItem.name;
													selectedItem.value = selectedItem.id;
													return selectedItem;
												} else {
													return { value: null, label: "Choose..." };
												}
											})()}
											items={userTypes
												.filter(item => item.id !== values.usertypeId)
												.map(data => ({
													...data,
													label: data.name,
													value: data.id,
												}))}
											onChange={item =>
												onSelectOptionChange(item, "usertypeId")
											}
										/>
									</div>
								</div>
								<div className="col-12 col-md-4 mt-2">
									<div className="form-group">
										<label>Time Zones</label>
										<ReactSelectOption
											value={values.tziidDisplayname}
											selectedValue={(() => {
												const selectedItem = {
													...timeZones.find(
														data =>
															data.displayName === values.tziidDisplayname,
													),
												};
												if (Object.keys(selectedItem).length) {
													selectedItem.label = selectedItem.name;
													selectedItem.value = selectedItem.displayName;
													return selectedItem;
												} else {
													return { value: null, label: "Choose..." };
												}
											})()}
											items={timeZones
												.filter(item => item.id !== values.tziidDisplayname)
												.map(data => ({
													...data,
													label: data.name,
													value: data.displayName,
												}))}
											onChange={item =>
												onSelectOptionChange(item, "tziidDisplayname")
											}
										/>
									</div>
								</div>
								<div className="col-12 col-md-4 mt-2">
									<div className="form-group">
										<label>Languages</label>
										<ReactSelectOption
											value={values.defaultLanguageId}
											selectedValue={(() => {
												const selectedItem = {
													...languages.find(
														data => data.id === values.defaultLanguageId,
													),
												};
												if (Object.keys(selectedItem).length) {
													selectedItem.label = selectedItem.name;
													selectedItem.value = selectedItem.id;
													return selectedItem;
												} else {
													return { value: null, label: "Choose..." };
												}
											})()}
											items={languages
												.filter(item => item.id !== values.defaultLanguageId)
												.map(data => ({
													...data,
													label: data.name,
													value: data.id,
												}))}
											onChange={item =>
												onSelectOptionChange(item, "defaultLanguageId")
											}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="col-12">
							<hr />
						</div>
						<div className="col-12">
							<div className="row mt-2">
								<div className="col-lg-4 col-md-6 col-lg-4 col-12 mb-2">
									<div className="form-group position-relative mb-0">
										<div className="course-media-wrapper">
											{uploadedImage && (
												<div className="upload-file-content">
													<div
														className="img"
														style={{
															backgroundImage: `url(${
																uploadedImage ? uploadedImage : ""
															})`,
														}}
														alt="course-image"></div>
													{uploadedImage ? (
														<div onClick={() => removeImg()}>
															<CloseSvg />
														</div>
													) : null}
												</div>
											)}
										</div>
										<UploadButton
											textInputClasses="pl-100"
											text={translationService.translate("TR_UPLOAD_FILE")}
											clicked={uploadImage}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="col-md-6 col-12 my-1">
							<Input
								type="text"
								id="username"
								name="username"
								inputClassName="pr--5"
								value={values.username}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={isInvalidSubmit && !values.name.trim().length}
								labelValue="Name *"
								onChange={event => onChange(event, "username")}
							/>
						</div>
						<div className="col-md-6 col-12 my-1">
							<div className="position-relative">
								<Input
									type="text"
									id="password"
									name="password"
									inputClassName="pr--5"
									value={values.password}
									// fieldLength={fieldsLength.nameMaxLength}
									isInvalidSubmit={
										isInvalidSubmit && !values.name.trim().length
									}
									labelValue="Password *"
									onChange={event => onChange(event, "password")}
								/>
								<button
									className="btn mindalay--btn-dark"
									style={{ position: "absolute", bottom: "6%", right: "6px" }}
									type="buton"
									onClick={e => {
										e.preventDefault();
										setIsInvalidSubmit(false);
										setValues(values => ({
											...values,
											password: generateRandomPassword(9),
										}));
									}}>
									Generate password
								</button>
							</div>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="lastname"
								name="lastname"
								inputClassName="pr--5"
								value={values.lastname}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={
									isInvalidSubmit && !values.lastname.trim().length
								}
								labelValue="Last name*"
								onChange={event => onChange(event, "lastname")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="firstname"
								name="firstname"
								inputClassName="pr--5"
								value={values.firstname}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={isInvalidSubmit && !values.name.trim().length}
								labelValue="First name*"
								onChange={event => onChange(event, "firstname")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="middlename"
								name="middlename"
								inputClassName="pr--5"
								value={values.middlename}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={isInvalidSubmit && !values.name.trim().length}
								labelValue="Middle name"
								onChange={event => onChange(event, "middlename")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="email"
								name="email"
								inputClassName="pr--5"
								value={values.email}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={isInvalidSubmit && !values.email.trim().length}
								labelValue="Email*"
								onChange={event => onChange(event, "email")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="date"
								id="dob"
								name="dob"
								inputClassName="pr--5"
								value={values.dob}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={isInvalidSubmit && !values.name.trim().length}
								labelValue="Date of birth"
								onChange={event => onChange(event, "date")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="phone"
								name="phone"
								inputClassName="pr--5"
								value={values.phone}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={isInvalidSubmit && !values.phone.trim().length}
								labelValue="Phone"
								onChange={event => onNumberChange(event, "phone")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="mobilePhone"
								name="mobilePhone"
								inputClassName="pr--5"
								value={values.mobilePhone}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={
									isInvalidSubmit && !values.mobilePhone.trim().length
								}
								labelValue="Mobile phone"
								onChange={event => onNumberChange(event, "mobilePhone")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="addressResidence"
								name="addressResidence"
								inputClassName="pr--5"
								value={values.addressResidence}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={
									isInvalidSubmit && !values.addressResidence.trim().length
								}
								labelValue="Address residence"
								onChange={event => onChange(event, "addressResidence")}
							/>
						</div>
						<div className="col-md-4 col-12 my-1">
							<Input
								type="text"
								id="addressRegistration"
								name="addressRegistration"
								inputClassName="pr--5"
								value={values.addressRegistration}
								// fieldLength={fieldsLength.nameMaxLength}
								isInvalidSubmit={
									isInvalidSubmit && !values.addressRegistration.trim().length
								}
								labelValue="Address registration"
								onChange={event => onChange(event, "addressRegistration")}
							/>
						</div>
						<div className="col-md-6 col-12 my-1">
							<InputCheckBox
								id={`sendEmail`}
								name={`sendEmail`}
								checked={values.sendEmail}
								labelValue={"Send Email"}
								onChange={event => {
									setValues({
										...values,
										sendEmail: event.target.checked,
									});
								}}
							/>
						</div>
						<div className="col-md-6 col-12 my-1">
							<InputCheckBox
								id={`sendNotification`}
								name={`sendNotification`}
								checked={values.sendNotification}
								labelValue={"Send Notification"}
								onChange={event => {
									setValues({
										...values,
										sendNotification: event.target.checked,
									});
								}}
							/>
						</div>
						<div className="col-12">
							<hr />
						</div>

						<div className="col-12">
							<div>
								<ActionButton
									spinnerId={buttonSpinnerId}
									type="submit"
									className="mindalay--btn-default float-right mb-4 px-5"
									name={translationService.translate("TR_SAVE")}
								/>
							</div>
						</div>
					</div>
				</form>
			</div>
		)
	);
}
export default withRouter(UserForm);
